import { AccountType } from '@constants'
import {
  IDocumentRelation,
  IRelatedDocumentRole,
  IRelationLinkKindMeta,
  IRelationType
} from '@query/documentRelations'

import { RelationLinkKind } from './relationContext'

/**
 * Accrual/settlement counterpart flip for ACCRUAL_PAIR and SALARY_PAYMENT.
 * Other relation types must supply their own pairing rules.
 */
export const getCounterpartRole = (
  role: IRelatedDocumentRole,
  relationType: IRelationType = IRelationType.ACCRUAL_PAIR
): IRelatedDocumentRole => {
  switch (relationType) {
    case IRelationType.ACCRUAL_PAIR:
    case IRelationType.SALARY_PAYMENT:
      return role === IRelatedDocumentRole.ACCRUAL
        ? IRelatedDocumentRole.SETTLEMENT
        : IRelatedDocumentRole.ACCRUAL
    default: {
      const exhaustive: never = relationType
      return exhaustive
    }
  }
}

/**
 * `role` here is the role of the document currently open (the suggestion source),
 * not the suggested counterpart — mirrors `IDocumentRelation.role` semantics.
 */
export const getSuggestionPromptKey = (
  role: IRelatedDocumentRole,
  relationType: IRelationType = IRelationType.ACCRUAL_PAIR
): string => {
  switch (relationType) {
    case IRelationType.SALARY_PAYMENT:
      return role === IRelatedDocumentRole.ACCRUAL
        ? 'documentRelations.suggestions.prompt.SALARY_PAYMENT.ACCRUAL'
        : 'documentRelations.suggestions.prompt.SALARY_PAYMENT.SETTLEMENT'
    case IRelationType.ACCRUAL_PAIR:
      return role === IRelatedDocumentRole.ACCRUAL
        ? 'documentRelations.suggestions.prompt.ACCRUAL'
        : 'documentRelations.suggestions.prompt.SETTLEMENT'
    default: {
      const exhaustive: never = relationType
      return exhaustive
    }
  }
}

/**
 * Link kinds come only from backend `available_link_kinds`.
 * No client-side account-type matrix — empty until API/preview metadata is available.
 */
export const getAvailableLinkKinds = (
  linkKindMetas?: Array<Pick<IRelationLinkKindMeta, 'kind'>> | null
): RelationLinkKind[] => linkKindMetas?.map(item => item.kind) ?? []

const fallbackRoleForKind = (kind: RelationLinkKind): IRelatedDocumentRole => {
  switch (kind) {
    case 'payment':
    case 'salaryPayment':
      return IRelatedDocumentRole.SETTLEMENT
    case 'salesInvoice':
    case 'purchaseInvoice':
    case 'salary':
      return IRelatedDocumentRole.ACCRUAL
    default: {
      const exhaustive: never = kind
      return exhaustive
    }
  }
}

export const getLinkKindRelationRole = (
  kind: RelationLinkKind,
  linkKindMetas?: IRelationLinkKindMeta[] | null
): IRelatedDocumentRole => {
  const meta = linkKindMetas?.find(item => item.kind === kind)
  return meta?.role ?? fallbackRoleForKind(kind)
}

export const getLinkKindRelationType = (
  kind: RelationLinkKind,
  linkKindMetas?: IRelationLinkKindMeta[] | null
): IRelationType => {
  const meta = linkKindMetas?.find(item => item.kind === kind)
  if (meta?.relation_type) {
    return meta.relation_type
  }
  return kind === 'salary' || kind === 'salaryPayment'
    ? IRelationType.SALARY_PAYMENT
    : IRelationType.ACCRUAL_PAIR
}

/**
 * Counterpart picker filters come only from backend link-kind metadata.
 * Missing meta → empty filters (picker stays disabled until metadata loads).
 */
export const getCounterpartAccountFilters = (
  kind: RelationLinkKind,
  linkKindMetas?: IRelationLinkKindMeta[] | null
): { accountTypes: AccountType[]; match: 'any' | 'all' } => {
  const meta = linkKindMetas?.find(item => item.kind === kind)
  if (!meta) {
    return { accountTypes: [], match: 'any' }
  }
  return {
    accountTypes: meta.account_types as AccountType[],
    match: meta.account_type_match
  }
}

export const getRelationsForLinkKind = (
  relations: Array<
    Pick<IDocumentRelation, 'role' | 'type' | 'related_document' | 'related_document_summary'> & {
      id?: number | null
    }
  >,
  kind: RelationLinkKind,
  linkKindMetas?: IRelationLinkKindMeta[] | null
) => {
  const role = getLinkKindRelationRole(kind, linkKindMetas)
  const relationType = getLinkKindRelationType(kind, linkKindMetas)
  return relations.filter(relation => relation.role === role && relation.type === relationType)
}
